import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Tags, Filter, ArrowUpCircle, ArrowDownCircle, RefreshCw, CheckCircle2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import ShelfTalkerModal from "@/components/ShelfTalkerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n === null || n === undefined ? "—" : `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(2)}%`);

export default function PriceSuggestions() {
  // Mode + numeric inputs
  const [mode, setMode] = useState("target"); // target | floor_target | custom
  const [targetMarkup, setTargetMarkup] = useState(49.73);
  const [floorMarkup, setFloorMarkup] = useState(25);

  // Custom overrides
  const [perSupplier, setPerSupplier] = useState({}); // { name: pct }
  const [perCategory, setPerCategory] = useState({});
  const [supplierList, setSupplierList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [categoryDepartments, setCategoryDepartments] = useState({}); // { category: [departments] }

  // Filters
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [excludeCategories, setExcludeCategories] = useState([]); // multi-exclude
  const [excludeDepartments, setExcludeDepartments] = useState([]); // multi-exclude
  const [onlyRecent, setOnlyRecent] = useState(false);
  const [recentDays, setRecentDays] = useState(90);
  const [excludeDiscontinued, setExcludeDiscontinued] = useState(true);
  const [excludeZeroStock, setExcludeZeroStock] = useState(false);
  const [minDelta, setMinDelta] = useState(0);
  const [onlyShowChanges, setOnlyShowChanges] = useState(true);

  // Result + selection
  const [loading, setLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({}); // product_id -> bool
  const [editedPrices, setEditedPrices] = useState({}); // product_id -> string
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Shelf talker — opened automatically after a successful price apply
  const [shelfTalkerItems, setShelfTalkerItems] = useState([]);
  const [showShelfTalker, setShowShelfTalker] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/price-suggestions/filter-options`);
        setSupplierList(res.data.suppliers || []);
        setCategoryList(res.data.categories || []);
        setDepartmentList(res.data.departments || []);
        setCategoryDepartments(res.data.category_departments || {});
      } catch (e) {
        toast.error("Failed to load suppliers/categories");
      }
    })();
  }, []);

  // Cascade: when a category is selected, only show departments belonging to
  // that category. "All categories" → full department list. Auto-reset the
  // department filter / excludes if they no longer match the chosen category.
  const visibleDepartments = useMemo(() => {
    if (filterCategory === "all") return departmentList;
    return categoryDepartments[filterCategory] || [];
  }, [filterCategory, departmentList, categoryDepartments]);

  useEffect(() => {
    if (filterCategory === "all") return;
    const allowed = new Set(categoryDepartments[filterCategory] || []);
    if (filterDepartment !== "all" && !allowed.has(filterDepartment)) {
      setFilterDepartment("all");
    }
    setExcludeDepartments((prev) => prev.filter((d) => allowed.has(d)));
  }, [filterCategory, categoryDepartments, filterDepartment]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setSelected({});
    setEditedPrices({});
    try {
      const body = {
        mode,
        target_markup_pct: Number(targetMarkup) || 0,
        floor_markup_pct: Number(floorMarkup) || 0,
        per_supplier_markup: mode === "custom" ? perSupplier : {},
        per_category_markup: mode === "custom" ? perCategory : {},
        filter_supplier: filterSupplier === "all" ? null : filterSupplier,
        filter_category: filterCategory === "all" ? null : filterCategory,
        filter_department: filterDepartment === "all" ? null : filterDepartment,
        exclude_categories: filterCategory === "all" ? excludeCategories : [],
        exclude_departments: filterDepartment === "all" ? excludeDepartments : [],
        only_with_recent_sales: onlyRecent,
        recent_sales_days: Number(recentDays) || 90,
        exclude_discontinued: excludeDiscontinued,
        exclude_zero_stock: excludeZeroStock,
        only_show_changes: onlyShowChanges,
        min_price_change_rand: Number(minDelta) || 0,
      };
      const res = await axios.post(`${API}/price-suggestions/analyze`, body);
      setResult(res.data);
      toast.success(`${res.data.suggestions.length} products listed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const suggestions = result?.suggestions || [];
  const summary = result?.summary;

  // ---- Selection helpers ----
  const allSelected = suggestions.length > 0 && suggestions.every((s) => selected[s.product_id]);
  const someSelected = suggestions.some((s) => selected[s.product_id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const next = {};
      suggestions.forEach((s) => { next[s.product_id] = true; });
      setSelected(next);
    }
  };
  const selectedRows = useMemo(
    () => suggestions.filter((s) => selected[s.product_id]),
    [suggestions, selected]
  );
  const selectedDeltaTotal = useMemo(
    () => selectedRows.reduce((acc, r) => acc + (Number(editedPrices[r.product_id]) || r.suggested_price_incl_vat) - r.current_price_incl_vat, 0),
    [selectedRows, editedPrices]
  );

  const handleApply = async () => {
    if (!selectedRows.length) return;
    setApplyLoading(true);
    try {
      const items = selectedRows.map((r) => ({
        product_id: r.product_id,
        new_price_incl_vat: Number(editedPrices[r.product_id]) || r.suggested_price_incl_vat,
      }));
      const res = await axios.post(`${API}/price-suggestions/apply`, {
        items,
        note: `Bulk repricing via Price Suggestions tool (mode=${mode})`,
      });
      const updated = res.data.updated || 0;
      const failedIds = new Set((res.data.failed || []).map((f) => f.product_id));
      toast.success(`${updated} prices updated`);
      if (res.data.failed?.length) toast.error(`${res.data.failed.length} failed`);
      setConfirmOpen(false);

      // Build shelf-talker items for every successfully repriced product so the
      // cashier can immediately reprint shelf tags with the new prices.
      const reprintRows = selectedRows.filter((r) => !failedIds.has(r.product_id));
      if (reprintRows.length > 0) {
        const fetched = await Promise.all(reprintRows.map(async (r) => {
          let barcode = "";
          let shelf_number, bin_number, location;
          try {
            const p = await axios.get(`${API}/products/${r.product_id}`);
            barcode = p.data?.barcode ? String(p.data.barcode).trim() : "";
            shelf_number = p.data?.shelf_number;
            bin_number = p.data?.bin_number;
            location = p.data?.location;
          } catch (_) { /* fall through with bare data */ }
          return {
            product_id: r.product_id,
            plu_code: r.plu_code,
            barcode,
            name: r.name,
            selling_price: Number(editedPrices[r.product_id]) || r.suggested_price_incl_vat,
            shelf_number,
            bin_number,
            location,
            print_shelf_talker: true,
          };
        }));
        setShelfTalkerItems(fetched);
        setShowShelfTalker(true);
      } else {
        // No reprintable rows — just refresh.
        fetchSuggestions();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplyLoading(false);
    }
  };

  // ---- Custom overrides UI helpers ----
  const setOverride = (kind, key, value) => {
    const updater = kind === "supplier" ? setPerSupplier : setPerCategory;
    updater((prev) => {
      const next = { ...prev };
      if (value === "" || value === null || value === undefined) delete next[key];
      else next[key] = Number(value);
      return next;
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="price-suggestions-page">
      <div className="flex items-center gap-3">
        <Tags className="h-7 w-7 text-indigo-600" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Price Suggestions & Bulk Repricing</h1>
          <p className="text-sm text-gray-500">Push every product up to a target markup. Review, edit, then apply selected.</p>
        </div>
      </div>

      {/* INPUTS */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Filter className="h-4 w-4" /> Inputs</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* Mode selector */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="w-[260px]" data-testid="select-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="target">Single target markup</SelectItem>
                  <SelectItem value="floor_target">Floor + target (whichever is higher)</SelectItem>
                  <SelectItem value="custom">Custom per supplier / category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Target markup % on cost</Label>
              <Input type="number" step="0.01" value={targetMarkup} onChange={(e) => setTargetMarkup(e.target.value)} className="w-[160px]" data-testid="input-target-markup" />
            </div>
            {mode === "floor_target" && (
              <div className="space-y-1">
                <Label>Floor markup %</Label>
                <Input type="number" step="0.01" value={floorMarkup} onChange={(e) => setFloorMarkup(e.target.value)} className="w-[160px]" data-testid="input-floor-markup" />
              </div>
            )}
          </div>

          {/* Custom overrides editor */}
          {mode === "custom" && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500">Per-supplier overrides</Label>
                <div className="max-h-56 overflow-y-auto rounded border bg-white">
                  {supplierList.map((s) => (
                    <div key={`sup-${s}`} className="flex items-center justify-between gap-2 px-2 py-1 border-b last:border-b-0 text-sm">
                      <span className="truncate" title={s}>{s}</span>
                      <Input
                        type="number" step="0.01" placeholder="—" className="w-24 h-7"
                        value={perSupplier[s] ?? ""}
                        onChange={(e) => setOverride("supplier", s, e.target.value)}
                        data-testid={`override-supplier-${s}`}
                      />
                    </div>
                  ))}
                  {!supplierList.length && <div className="p-2 text-xs text-gray-500">No suppliers found.</div>}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500">Per-category overrides</Label>
                <div className="max-h-56 overflow-y-auto rounded border bg-white">
                  {categoryList.map((c) => (
                    <div key={`cat-${c}`} className="flex items-center justify-between gap-2 px-2 py-1 border-b last:border-b-0 text-sm">
                      <span className="truncate" title={c}>{c}</span>
                      <Input
                        type="number" step="0.01" placeholder="—" className="w-24 h-7"
                        value={perCategory[c] ?? ""}
                        onChange={(e) => setOverride("category", c, e.target.value)}
                        data-testid={`override-category-${c}`}
                      />
                    </div>
                  ))}
                  {!categoryList.length && <div className="p-2 text-xs text-gray-500">No categories found.</div>}
                </div>
                <p className="text-xs text-gray-500">Category beats supplier beats target.</p>
              </div>
            </div>
          )}

          {/* Filters row */}
          <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
            <div className="space-y-1">
              <Label>Filter supplier</Label>
              <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                <SelectTrigger className="w-[220px]" data-testid="filter-supplier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All suppliers</SelectItem>
                  {supplierList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Filter category</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[220px]" data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categoryList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Filter department</Label>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-[220px]" data-testid="filter-department"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All departments</SelectItem>
                  {visibleDepartments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              {filterCategory !== "all" && (
                <p className="text-[10px] text-gray-500">Showing {visibleDepartments.length} department(s) in {filterCategory}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label>Exclude categories</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button" variant="outline"
                    className="w-[240px] justify-start h-10 font-normal"
                    disabled={filterCategory !== "all"}
                    title={filterCategory !== "all" ? "Disabled: a single category filter is already set" : "Pick categories to exclude"}
                    data-testid="btn-exclude-categories"
                  >
                    {excludeCategories.length
                      ? `${excludeCategories.length} excluded`
                      : "None excluded"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-0">
                  <div className="px-3 py-2 border-b flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-500">Exclude categories</span>
                    {excludeCategories.length > 0 && (
                      <button
                        type="button" className="text-xs text-indigo-600 hover:underline"
                        onClick={() => setExcludeCategories([])}
                        data-testid="btn-clear-excluded"
                      >Clear</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {categoryList.length === 0 && <div className="p-3 text-xs text-gray-500">No categories found.</div>}
                    {categoryList.map((c) => {
                      const checked = excludeCategories.includes(c);
                      return (
                        <label key={`exc-${c}`} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setExcludeCategories((prev) =>
                                v ? Array.from(new Set([...prev, c])) : prev.filter((x) => x !== c)
                              );
                            }}
                            data-testid={`cb-exclude-${c}`}
                          />
                          <span className="truncate" title={c}>{c}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              {excludeCategories.length > 0 && filterCategory === "all" && (
                <div className="flex flex-wrap gap-1 pt-1 max-w-[240px]">
                  {excludeCategories.map((c) => (
                    <Badge key={`chip-${c}`} variant="secondary" className="text-[10px] gap-1 pr-1">
                      {c}
                      <button
                        type="button" className="hover:text-red-600"
                        onClick={() => setExcludeCategories((prev) => prev.filter((x) => x !== c))}
                        aria-label={`Remove ${c}`}
                      ><XIcon className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label>Exclude departments</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button" variant="outline"
                    className="w-[240px] justify-start h-10 font-normal"
                    disabled={filterDepartment !== "all"}
                    title={filterDepartment !== "all" ? "Disabled: a single department filter is already set" : "Pick departments to exclude"}
                    data-testid="btn-exclude-departments"
                  >
                    {excludeDepartments.length
                      ? `${excludeDepartments.length} excluded`
                      : "None excluded"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-0">
                  <div className="px-3 py-2 border-b flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-gray-500">Exclude departments</span>
                    {excludeDepartments.length > 0 && (
                      <button
                        type="button" className="text-xs text-indigo-600 hover:underline"
                        onClick={() => setExcludeDepartments([])}
                      >Clear</button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {visibleDepartments.length === 0 && <div className="p-3 text-xs text-gray-500">No departments found.</div>}
                    {visibleDepartments.map((d) => {
                      const checked = excludeDepartments.includes(d);
                      return (
                        <label key={`exc-dep-${d}`} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setExcludeDepartments((prev) =>
                                v ? Array.from(new Set([...prev, d])) : prev.filter((x) => x !== d)
                              );
                            }}
                            data-testid={`cb-exclude-dep-${d}`}
                          />
                          <span className="truncate" title={d}>{d}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
              {excludeDepartments.length > 0 && filterDepartment === "all" && (
                <div className="flex flex-wrap gap-1 pt-1 max-w-[240px]">
                  {excludeDepartments.map((d) => (
                    <Badge key={`chip-dep-${d}`} variant="secondary" className="text-[10px] gap-1 pr-1">
                      {d}
                      <button
                        type="button" className="hover:text-red-600"
                        onClick={() => setExcludeDepartments((prev) => prev.filter((x) => x !== d))}
                        aria-label={`Remove ${d}`}
                      ><XIcon className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="recent" checked={onlyRecent} onCheckedChange={(v) => setOnlyRecent(!!v)} data-testid="cb-recent" />
              <Label htmlFor="recent" className="cursor-pointer">Only items sold in last</Label>
              <Input type="number" value={recentDays} onChange={(e) => setRecentDays(e.target.value)} className="w-20 h-8" disabled={!onlyRecent} />
              <span className="text-sm text-gray-500">days</span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="disc" checked={excludeDiscontinued} onCheckedChange={(v) => setExcludeDiscontinued(!!v)} data-testid="cb-discontinued" />
              <Label htmlFor="disc" className="cursor-pointer">Exclude discontinued</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="zerostock" checked={excludeZeroStock} onCheckedChange={(v) => setExcludeZeroStock(!!v)} data-testid="cb-zero-stock" />
              <Label htmlFor="zerostock" className="cursor-pointer">Exclude zero-stock items</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="changes" checked={onlyShowChanges} onCheckedChange={(v) => setOnlyShowChanges(!!v)} data-testid="cb-only-changes" />
              <Label htmlFor="changes" className="cursor-pointer">Only show price changes</Label>
            </div>
            <div className="space-y-1">
              <Label>Min Rand change</Label>
              <Input type="number" step="0.01" value={minDelta} onChange={(e) => setMinDelta(e.target.value)} className="w-24" />
            </div>
            <Button onClick={fetchSuggestions} disabled={loading} data-testid="btn-analyze" className="ml-auto">
              {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Generate suggestions
            </Button>
          </div>
          <p className="text-xs text-gray-500">Direct/Special Order items are always excluded.</p>
        </CardContent>
      </Card>

      {/* SUMMARY */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-4">
            <div className="text-xs text-gray-500">Eligible products</div>
            <div className="text-2xl font-semibold">{summary.total_eligible_products.toLocaleString()}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-xs text-gray-500">Will be repriced</div>
            <div className="text-2xl font-semibold" data-testid="summary-changes">{summary.suggestions_count.toLocaleString()}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-xs text-gray-500 flex items-center gap-1"><ArrowUpCircle className="h-3 w-3 text-green-600" /> Increases</div>
            <div className="text-2xl font-semibold text-green-700">{summary.would_increase_count}</div>
            <div className="text-xs text-gray-500">{fmt(summary.total_rand_increase)} total uplift</div>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <div className="text-xs text-gray-500 flex items-center gap-1"><ArrowDownCircle className="h-3 w-3 text-amber-600" /> Decreases</div>
            <div className="text-2xl font-semibold text-amber-700">{summary.would_decrease_count}</div>
            <div className="text-xs text-gray-500">{fmt(summary.total_rand_decrease)}</div>
          </CardContent></Card>
        </div>
      )}

      {/* RESULTS */}
      {suggestions.length > 0 && (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Suggested prices ({suggestions.length})</CardTitle>
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={!someSelected}
              data-testid="btn-apply-selected"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Apply selected ({selectedRows.length})
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="cb-select-all" />
                  </TableHead>
                  <TableHead>PLU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Cost (excl)</TableHead>
                  <TableHead className="text-right">Current Price</TableHead>
                  <TableHead className="text-right">Cur. Markup</TableHead>
                  <TableHead className="text-right">Suggested Price</TableHead>
                  <TableHead className="text-right">New Markup</TableHead>
                  <TableHead className="text-right">Δ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((s) => {
                  const isUp = s.delta_rand > 0;
                  const isDown = s.delta_rand < 0;
                  const editVal = editedPrices[s.product_id];
                  return (
                    <TableRow key={s.product_id} data-testid={`row-${s.product_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={!!selected[s.product_id]}
                          onCheckedChange={(v) => setSelected((p) => ({ ...p, [s.product_id]: !!v }))}
                          data-testid={`cb-${s.product_id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.plu_code}</TableCell>
                      <TableCell className="max-w-[260px] truncate" title={s.name}>
                        {s.name}
                        {s.is_discontinued && <Badge variant="outline" className="ml-2 text-[10px]">DISC</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">{s.supplier}</TableCell>
                      <TableCell className="text-sm">{s.category}</TableCell>
                      <TableCell className="text-sm">{s.department}</TableCell>
                      <TableCell className={`text-right text-sm ${Number(s.current_stock) <= 0 ? "text-red-600 font-medium" : ""}`} data-testid={`stock-${s.product_id}`}>
                        {Number(s.current_stock ?? 0).toLocaleString("en-ZA")}
                      </TableCell>
                      <TableCell className="text-right">{fmt(s.cost_excl_vat)}</TableCell>
                      <TableCell className="text-right">{fmt(s.current_price_incl_vat)}</TableCell>
                      <TableCell className="text-right text-xs text-gray-500">{pct(s.current_markup_pct)}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number" step="0.01"
                          className="w-24 h-7 text-right ml-auto"
                          value={editVal ?? s.suggested_price_incl_vat}
                          onChange={(e) => setEditedPrices((p) => ({ ...p, [s.product_id]: e.target.value }))}
                          data-testid={`price-${s.product_id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-gray-500">{pct(s.applied_markup_pct)}</TableCell>
                      <TableCell className={`text-right font-semibold ${isUp ? "text-green-700" : isDown ? "text-amber-700" : ""}`}>
                        {isUp ? "+" : ""}{fmt(s.delta_rand)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {!loading && result && !suggestions.length && (
        <Card><CardContent className="py-10 text-center text-sm text-gray-500">
          No products to reprice with these inputs. Try lowering the target, turning off "Only show price changes", or relaxing filters.
        </CardContent></Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Apply {selectedRows.length} price changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Net price-list impact: <strong>{selectedDeltaTotal >= 0 ? "+" : ""}{fmt(selectedDeltaTotal)}</strong> across selected items.
              Every change is logged to the audit trail and is reversible from there.
              <br />
              <span className="text-xs text-indigo-600">After applying, a Shelf-Talker print dialog will open automatically for the repriced products.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applyLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApply} disabled={applyLoading} data-testid="confirm-apply">
              {applyLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShelfTalkerModal
        open={showShelfTalker}
        onClose={() => {
          setShowShelfTalker(false);
          setShelfTalkerItems([]);
          fetchSuggestions();
        }}
        items={shelfTalkerItems}
      />
    </div>
  );
}
